import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  InputAdornment,
  Tooltip,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  ContentCopy as CopyIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import PhoneNumberField from "../components/common/PhoneNumberField";
import { RootState, AppDispatch } from "../store";
import { Client, ClientCreateInput } from "../services/clientsService";
import clientsService from "../services/clientsService";
import {
  fetchClients,
  createClient,
  updateClient,
  deleteClient,
} from "../store/slices/clientsSlice";
import {
  generatePublicAccessCode,
  generatePublicAccessLink,
} from "../utils/passwordGenerator";
import { useToast } from "../hooks/useToast";
import { useConfirm } from "../contexts/ConfirmDialogContext";
import { getApiErrorMessage, getApiFieldErrors } from "../utils/apiError";

interface FormData {
  first_name: string;
  last_name: string;
  position: string;
  company: string;
  email: string;
  phone_no: string;
  status: boolean;
  password: string;
  confirm_password: string;
  is_user: boolean;
  enable_public_access: boolean;
  public_access_code?: string;
}

interface FormErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_no?: string;
  password?: string;
  confirm_password?: string;
}

const initialFormData: FormData = {
  first_name: "",
  last_name: "",
  position: "",
  company: "",
  email: "",
  phone_no: "",
  status: true,
  password: "",
  confirm_password: "",
  is_user: true,
  enable_public_access: false,
};

const Clients = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const confirm = useConfirm();
  const { clients, loading } = useSelector((state: RootState) => state.clients);
  const { showSuccess, showError } = useToast();

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [publicAccessLink, setPublicAccessLink] = useState<string>("");
  const [linkCopied, setLinkCopied] = useState(false);
  const { user, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );

  useEffect(() => {
    // Replace with actual user ID
    const userId = user?.user_id || "";
    dispatch(fetchClients(userId));
    loadRoles();
    loadPositions();
  }, [dispatch]);

  const loadRoles = async () => {
    try {
      const rolesData = await clientsService.getRoles();
      setRoles(rolesData);
    } catch (err: any) {
      console.error("Failed to load roles:", err);
    }
  };

  const loadPositions = async () => {
    try {
      const positionsData = await clientsService.getPositions();
      setPositions(positionsData);
    } catch (err: any) {
      console.error("Failed to load positions:", err);
    }
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredClients(clients);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = clients.filter((client) => {
      return (
        client.first_name?.toLowerCase().includes(query) ||
        client.last_name?.toLowerCase().includes(query) ||
        client.email?.toLowerCase().includes(query) ||
        client.company?.toLowerCase().includes(query) ||
        client.profile?.toLowerCase().includes(query) ||
        client.phone_no?.toLowerCase().includes(query)
      );
    });
    setFilteredClients(filtered);
  }, [searchQuery, clients]);

  const handleOpenDialog = (clientData?: Client) => {
    if (clientData) {
      setSelectedClient(clientData);
      setFormData({
        first_name: clientData.first_name,
        last_name: clientData.last_name,
        position: clientData.profile,
        status: clientData.status,
        company: clientData.company,
        email: clientData.email,
        phone_no: clientData.phone_no,
        password: "",
        confirm_password: "",
        is_user: (clientData as any).is_user || true,
        enable_public_access: false,
      });
    } else {
      setSelectedClient(null);
      setFormData(initialFormData);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedClient(null);
    setFormData(initialFormData);
    setFormErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      errors.first_name = t("validation.required");
    }

    if (!formData.last_name.trim()) {
      errors.last_name = t("validation.required");
    }

    if (!formData.email.trim()) {
      errors.email = t("auth.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t("auth.validEmail");
    }

    // Validate phone number (international format)
    if (
      formData.phone_no &&
      !/^\+?[1-9]\d{1,14}$/.test(formData.phone_no.replace(/\s/g, ""))
    ) {
      errors.phone_no = t("validation.phoneInvalid");
    }

    // Validate passwords for new clients or when password is being changed
    // if (!selectedClient && !formData.password) {
    //   errors.password = t('validation.required');
    // } else if (formData.password && formData.password.length < 8) {
    //   errors.password = t('validation.minLength', { min: 8 });
    // }

    if (formData.password && formData.password !== formData.confirm_password) {
      errors.confirm_password = t("validation.passwordMismatch");
    }
    console.log("ERRORS", errors);

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleFieldChange = (
    field: keyof FormData,
    value: string | boolean,
  ) => {
    setFormData({ ...formData, [field]: value });
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handlePublicAccessToggle = (enabled: boolean) => {
    handleFieldChange("enable_public_access", enabled);

    if (enabled && !formData.public_access_code) {
      const accessCode = generatePublicAccessCode();
      setFormData({
        ...formData,
        password: accessCode,
        confirm_password: accessCode,
      });
      handleFieldChange("public_access_code", accessCode);

      // Generate the public access link (we'll need a case ID for this)
      // For now, we'll show the access code
    }
  };

  const copyPublicAccessLink = async () => {
    if (publicAccessLink) {
      try {
        await navigator.clipboard.writeText(publicAccessLink);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy link:", err);
      }
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // Validate passwords for new clients or when password is being changed
    // if (!selectedClient && !formData.password) {
    //   showError("Password is required for new client access");
    //   return;
    // }

    // if (formData.password && formData.password !== formData.confirm_password) {
    //   showError("Passwords do not match");
    //   return;
    // }

    const userId = user?.user_id || ""; // Replace with actual user ID

    // Only include password in the data if it's provided (for new clients or password changes)
    const clientData: any = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      profile: formData.position,
      status: formData.status,
      company: formData.company,
      email: formData.email,
      phone_no: formData.phone_no,
      is_user: formData.is_user,
      password: generatePublicAccessCode(),
    };

    // Only include password if it's provided (new client or password change)
    if (formData.password) {
      clientData.password = formData.password;
    }

    try {
      if (selectedClient) {
        await dispatch(
          updateClient({
            id: selectedClient.id,
            user_id: userId,
            ...clientData,
          }),
        ).unwrap();
        showSuccess(t("clients.clientUpdated"));
      } else {
        await dispatch(createClient(clientData)).unwrap();
        showSuccess(t("clients.clientCreated"));
      }
      dispatch(fetchClients(userId));
      handleCloseDialog();
    } catch (error: any) {
      // A 422 comes back as `{ errors: { email: ["The email has already
      // been taken."] } }` — surface that on the actual field (so the user
      // sees *why*, on the email input) as well as in the toast, instead of
      // just the generic top-level "Validation failed" message.
      const fieldErrors = getApiFieldErrors(error);
      if (Object.keys(fieldErrors).length > 0) {
        setFormErrors((prev) => ({ ...prev, ...fieldErrors }));
      }
      showError(getApiErrorMessage(error, t("clients.errorSavingClient")));
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm(t("clients.confirmDeleteClient"));
    if (ok) {
      try {
        // Replace with actual user ID
        await dispatch(deleteClient({ id, userId: user?.user_id || "" })).unwrap();
        showSuccess(t("clients.clientDeleted"));
      } catch (error: any) {
        showError(getApiErrorMessage(error, t("clients.errorDeletingClient")));
      }
    }
  };

  const getPositionOptions = () => {
    if (positions.length > 0) {
      return positions.map((position: any) => ({
        value: position.name,
        label: position.display_name || position.name,
      }));
    }

    // Fallback to hardcoded positions if backend data not available
    return [
      { value: "Partner", label: t("clients.positionPartner") },
      { value: "Senior Partner", label: t("clients.positionSeniorPartner") },
      { value: "Paralegal", label: t("clients.positionParalegal") },
      { value: "Lawyer", label: t("clients.positionLawyer") },
      { value: "Senior Lawyer", label: t("clients.positionSeniorLawyer") },
      { value: "Legal Assistant", label: t("clients.positionLegalAssistant") },
      { value: "Administrator", label: t("clients.positionAdministrator") },
      { value: "Other", label: t("clients.positionOther") },
    ];
  };

  const columns: GridColDef[] = [
    {
      field: "first_name",
      headerName: t("clients.firstName"),
      width: 200,
    },
    { field: "profile", headerName: t('common.position'), width: 150 },
    { field: "company", headerName: t('clients.company'), width: 150 },
    { field: "email", headerName: t('common.email'), width: 200 },
    { field: "phone_no", headerName: t('common.phone'), width: 150 },
    {
      field: "created_at",
      headerName: t('clients.createdAt'),
      width: 180,
    },
    {
      field: "actions",
      headerName: t("common.actions"),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const row = params?.row;
        if (!row) return null;
        return (
          <Box>
            <Tooltip title={t('common.edit')}>
              <IconButton onClick={() => handleOpenDialog(row)} size="small">
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <IconButton
                onClick={() => handleDelete(row.id)}
                size="small"
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">{t("navigation.clients")}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t("clients.addClient")}
        </Button>
      </Box>

      <Card sx={{ mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            name="clients-search"
            autoComplete="off"
            placeholder={t("clients.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 600 }}
          />
        </Box>
      </Card>

      <Card>
        <DataGrid
          rows={filteredClients}
          columns={columns}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10]}
          checkboxSelection
          disableRowSelectionOnClick
          autoHeight
          loading={loading}
        />
      </Card>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {selectedClient ? t("clients.editClient") : t("clients.addClient")}
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t('clients.companyDetails', 'Company Details')}
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  select
                  label={t('common.position')}
                  fullWidth
                  value={formData.position}
                  onChange={(e) =>
                    setFormData({ ...formData, position: e.target.value })
                  }
                >
                  {getPositionOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={t('clients.company')}
                  fullWidth
                  value={formData.company}
                  onChange={(e) =>
                    setFormData({ ...formData, company: e.target.value })
                  }
                />
              </Box>
            </Box>

            {/* Contact Information — grouped and consistently laid out
                (previously: email alone in a full-width row, phone in a
                differently-sized flex box, mismatched styling between
                them). Name doubles as the contact person for the client,
                so it lives in this section rather than a separate one. */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t('clients.contactInformation', 'Contact Information')}
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label={t('clients.contactFirstName', 'Contact First Name')}
                    fullWidth
                    name="client-first-name"
                    autoComplete="off"
                    value={formData.first_name}
                    onChange={(e) =>
                      handleFieldChange("first_name", e.target.value)
                    }
                    error={!!formErrors.first_name}
                    helperText={formErrors.first_name}
                    required
                  />
                  <TextField
                    label={t('clients.contactLastName', 'Contact Last Name')}
                    fullWidth
                    name="client-last-name"
                    autoComplete="off"
                    value={formData.last_name}
                    onChange={(e) => handleFieldChange("last_name", e.target.value)}
                    error={!!formErrors.last_name}
                    helperText={formErrors.last_name}
                    required
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    type="email"
                    label={t('common.email')}
                    fullWidth
                    // Distinct `name` + `autoComplete="off"` (plus the same
                    // on the client-list search box above) — without them,
                    // both were plain unnamed inputs rendered outside any
                    // `<form>` (this Dialog portals to `document.body`,
                    // right alongside the search box), which is exactly the
                    // shape Chrome's autofill groups together and fills as
                    // one "form": typing an email here was being echoed
                    // into the search box behind the dialog.
                    name="client-email"
                    autoComplete="off"
                    value={formData.email}
                    onChange={(e) => handleFieldChange("email", e.target.value)}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    required
                  />
                  <Box sx={{ flex: 1 }}>
                    <PhoneNumberField
                      value={formData.phone_no}
                      onChange={(value) =>
                        setFormData({ ...formData, phone_no: value })
                      }
                      error={formErrors.phone_no}
                      helperText={t('users.enterPhoneWithCountry')}
                      required
                    />
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked ={formData.status ?? true}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status: e.target.checked, // boolean: true / false
                      })
                    }
                    color="primary"
                  />
                }
                label={formData.status ? t('clients.statusActive') : t('clients.statusInactive')}
              />
            </Box>

            {/* <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={formData.password}
                onChange={(e) => handleFieldChange('password', e.target.value)}
                error={!!formErrors.password}
                helperText={formErrors.password || (
                  !selectedClient
                    ? "Required for new client access"
                    : "Leave blank to keep current password"
                )}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                value={formData.confirm_password}
                onChange={(e) => handleFieldChange('confirm_password', e.target.value)}
                error={!!formErrors.confirm_password}
                helperText={formErrors.confirm_password || (
                  formData.password &&
                  formData.confirm_password &&
                  formData.password !== formData.confirm_password
                    ? "Passwords do not match"
                    : ""
                )}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={toggleConfirmPasswordVisibility}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box> */}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedClient ? t('common.edit') : t('common.add')} {t('navigation.clients').toUpperCase()}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Clients;
